import { ArrowLeft } from "lucide-react";
import {
  useNavigate,
  useRouter,
  type LinkOptions,
} from "@tanstack/react-router";

interface BackButtonProps {
  /** Where to navigate when there is no in-app history (direct page load,
   *  bookmark, refresh on this page, etc.). Pass a typed route string
   *  ("/sales", "/tools/imports/new") for the common case, or the full
   *  LinkOptions object when you need params/search. */
  fallback: LinkOptions["to"] | LinkOptions;
  label?: string;
  /** "compact" matches the small "← Back" pill used in narrow headers;
   *  "wide" matches the icon-on-hover pill used in detail-page headers. */
  variant?: "compact" | "wide";
}

/**
 * Renders an ArrowLeft button that goes back in the browser history when
 * possible, otherwise navigates to `fallback`. TanStack Router stores its
 * navigation depth in `history.state.__TSR_index`; a value > 0 means this
 * isn't the first entry in the session, so `history.back()` will land
 * somewhere useful instead of doing nothing on a direct-loaded page.
 */
export function BackButton({
  fallback,
  label = "Back",
  variant = "compact",
}: BackButtonProps) {
  const router = useRouter();
  const navigate = useNavigate();

  const onClick = () => {
    const state =
      typeof window !== "undefined"
        ? (window.history.state as { __TSR_index?: number } | null)
        : null;
    const idx = state?.__TSR_index ?? 0;
    if (idx > 0) {
      router.history.back();
    } else {
      // navigate's generic-inference can't unify `string | LinkOptions` cleanly;
      // both branches resolve to a known typed-route at runtime.
      const target = typeof fallback === "string" ? { to: fallback } : fallback;
      void navigate(target as LinkOptions);
    }
  };

  const className =
    variant === "wide"
      ? "inline-flex h-9 items-center gap-2 rounded-md px-3 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
      : "inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground";
  const iconSize = variant === "wide" ? "h-5 w-5" : "h-4 w-4";

  return (
    <button type="button" onClick={onClick} className={className}>
      <ArrowLeft className={iconSize} /> {label}
    </button>
  );
}
